import type { ContextType } from "react";
import { PureComponent } from "react";

import { noop, range, values } from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import { Button, BulletPager } from "@similarweb/ui-components";

import { isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { ContactUsInlineTitle } from "app/components/React/ContactUs/StyledComponents";
import { TrackContext } from "pro-features/components/WithTrack/src/context";
import I18n from "pro-features/components/WithTranslation/src/I18n";

import ContactUsInline from "../../../../../app/components/React/ContactUs/ContactUsInline";
import { ProModal } from "../ProModal";
import TrialHookModal from "../TrialHookModal";
import type { IUnlockModalProps, IUnlockModalSlide, IUnlockModalState } from "../types";
import { UnlockModalState } from "../types";

import {
    Fade,
    FadeItemWrapper,
    proModalStyles,
    UnlockModalContent,
    UnlockModalContentSubtitle,
    UnlockModalContentTitle,
    UnlockModalHeader,
    UnlockModalImage,
    UnlockModalTitle,
    UnlockModalTitleWrap,
    UnlockModalWrap,
} from "./StyledComponents";

class UnlockModal extends PureComponent<IUnlockModalProps, IUnlockModalState> {
    static contextType = TrackContext;

    context: ContextType<typeof TrackContext>;

    private startSlide = 1;

    constructor(props: IUnlockModalProps) {
        super(props);

        this.state = {
            page: this.startSlide,
            current: UnlockModalState.UnlockInfo,
            currentSlide: null,
            submitted: false,
        };
    }

    public handleKeyUp = (e: KeyboardEvent) => {
        const { page } = this.state;
        const slides: IUnlockModalSlide[] = values(this.props.slides);

        if (e.keyCode === 39 && page < slides.length) {
            this.setPage(slides, page + 1);
        }
        if (e.keyCode === 37 && page > this.startSlide) {
            this.setPage(slides, page - 1);
        }
    };

    public componentDidMount() {
        // TODO: Implement images caching
        // this.slides.forEach( slide => {
        //     if (slide.img) {
        //         const cachedImage = new Image();
        //         cachedImage.src = slide.img;
        //     }
        // });

        const { activeSlide, slides } = this.props;
        const activeSlideState = this.getActiveSlideState(slides, activeSlide);
        this.setState({ ...activeSlideState });

        document.addEventListener("keyup", this.handleKeyUp, { capture: true });
    }

    public componentDidUpdate(prevProps: Readonly<IUnlockModalProps>) {
        const { activeSlide, slides } = this.props;
        const activeSlideState = this.getActiveSlideState(slides, activeSlide);
        if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen) {
            this.setState({
                current: UnlockModalState.UnlockInfo,
                submitted: false,
                ...activeSlideState,
            });
        }
    }

    public componentWillUnmount() {
        document.removeEventListener("keyup", this.handleKeyUp);
    }

    public onCtaClick = () => {
        this.setState({ current: UnlockModalState.ContactUs });
        this.trackModalEvent("open");
        if (typeof this.props.onCtaClickTrack === "function") {
            this.props.onCtaClickTrack();
        }
    };

    public onCloseModalClick = () => {
        this.props.onCloseClick?.();
        this.trackModalEvent("close");
    };

    public onCancelClick = () => {
        this.props.onCloseClick?.();
        this.trackModalEvent("cancel");
    };

    public render() {
        if (isTrial()) {
            return <TrialHookModal {...this.props} />;
        }

        const { isOpen, onAfterOpen = noop } = this.props;
        const slides: IUnlockModalSlide[] = values(this.props.slides);
        let content;

        switch (this.state.current) {
            case UnlockModalState.ContactUs:
                content = this.renderContactUsForm();
                break;
            case UnlockModalState.UnlockInfo:
            default:
                content = this.renderUnlockInfo(slides);
        }

        return (
            <ProModal
                isOpen={isOpen}
                onCloseClick={this.onCloseModalClick}
                customStyles={proModalStyles}
                onAfterOpen={onAfterOpen}
            >
                {content}
            </ProModal>
        );
    }

    private setPage = (slides: IUnlockModalSlide[], page: number) => {
        const currentSlide: IUnlockModalSlide = slides[page - 1];

        this.setState({ page, currentSlide }, () => {
            this.trackModalEvent("switch");
        });
    };

    private getActiveSlideState = (slides: FixmeType, active: FixmeType) => {
        const slidesValues: IUnlockModalSlide[] = values(slides);
        let index = 0;
        if (active) {
            index = slidesValues.indexOf(slides[active]);
        }
        return {
            page: index + 1,
            currentSlide: slidesValues[index],
        };
    };

    private isModalImageExist = (slides: IUnlockModalSlide[]) => slides.some((slide) => "img" in slide);

    private renderImg = (slides: IUnlockModalSlide[]) => {
        if (slides.length && this.isModalImageExist(slides)) {
            return (
                <UnlockModalImage>
                    {range(1, slides.length + 1).map((pageNum) => (
                        <Fade
                            key={pageNum}
                            in={pageNum === this.state.page}
                        >
                            <FadeItemWrapper hidden={pageNum !== this.state.page}>
                                <img
                                    width={slides[pageNum - 1]?.imgWidth ?? 420}
                                    src={slides[pageNum - 1].img}
                                    srcSet={slides[pageNum - 1].img2x ? `${slides[pageNum - 1].img2x} 2x` : undefined}
                                    alt="feature-illustration"
                                />
                            </FadeItemWrapper>
                        </Fade>
                    ))}
                </UnlockModalImage>
            );
        }
    };

    private renderText = (slides: IUnlockModalSlide[]) => {
        return range(1, slides.length + 1).map((pageNum) => (
            <Fade
                key={pageNum}
                in={pageNum === this.state.page}
            >
                <FadeItemWrapper hidden={pageNum !== this.state.page}>
                    <UnlockModalContentTitle>{slides[pageNum - 1].subtitle}</UnlockModalContentTitle>
                    <UnlockModalContentSubtitle>{slides[pageNum - 1].text.split("<br>")[0]}</UnlockModalContentSubtitle>
                </FadeItemWrapper>
            </Fade>
        ));
    };

    private renderContactUsForm = () => {
        const { label, location } = this.props;

        const customHeader = (
            <ContactUsInlineTitle>
                <I18n>hook_unlock.contact_us.title</I18n>
            </ContactUsInlineTitle>
        );

        return (
            <ContactUsInline
                location={location}
                label={label}
                // @ts-ignore FIXME: strict migration - '(() => void) | undefined' not assignable to '() => void' — onCloseClick is an optional prop, ContactUsInline requires a handler
                onClose={this.props.onCloseClick}
                customHeader={customHeader}
            />
        );
    };

    private renderTitle = (slides: IUnlockModalSlide[]) => {
        if (slides.length && this.isModalImageExist(slides)) {
            return range(1, slides.length + 1).map((pageNum) => (
                <Fade
                    key={pageNum}
                    in={pageNum === this.state.page}
                >
                    <UnlockModalTitle hidden={pageNum !== this.state.page}>
                        {slides[pageNum - 1].title}
                        <SWReactIcons
                            iconName="locked"
                            size="sm"
                        />
                    </UnlockModalTitle>
                </Fade>
            ));
        }
    };

    private renderUnlockInfo = (slides: IUnlockModalSlide[]) => {
        const { ctaText } = this.props;
        return (
            <UnlockModalWrap>
                {this.renderImg(slides)}
                <UnlockModalHeader>
                    <UnlockModalTitleWrap>{this.renderTitle(slides)}</UnlockModalTitleWrap>
                    <Button
                        onClick={this.onCtaClick}
                        type="upsell"
                    >
                        {ctaText}
                    </Button>
                </UnlockModalHeader>
                <UnlockModalContent>{this.renderText(slides)}</UnlockModalContent>
                {slides.length > 1 ? (
                    <BulletPager
                        pages={slides.length}
                        page={this.state.page}
                        onClick={(page) => {
                            this.setPage(slides, page);
                        }}
                    />
                ) : null}
            </UnlockModalWrap>
        );
    };

    private trackModalEvent(action: string) {
        const current: IUnlockModalSlide | null = this.state.currentSlide;

        if (current && this.props.isOpen) {
            const title = current.trackId || current.title || current.subtitle;
            // @ts-ignore FIXME: strict migration - 'this.context' possibly undefined — component always rendered within a TrackContext provider
            this.context.track("hook/Contact Us/description screen", action, title);
        }
    }
}

export default UnlockModal;
